import { Inject, NgModule, Optional, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { RegionService } from '../region.service';
import { TransferHttpCacheModule } from '@nguniversal/common';
import { translateBrowserLoaderFactory } from '../translate-browser.loader';
import { TransferState, makeStateKey } from '@angular/platform-browser';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { CookieService } from 'ngx-cookie';

/** Language the server resolved for this render (Accept-Language + geo). */
const SSR_LANG_KEY = makeStateKey<string>('uptal-lang');

@NgModule({
  imports: [
    HttpClientModule,
    TransferHttpCacheModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: translateBrowserLoaderFactory,
        deps: [HttpClient, TransferState],
      },
    }),
  ],
  exports: [TranslateModule],
})
export class I18nModule {
  storageKey = 'uptal-locale';
  constructor(
    private translate: TranslateService,
    private regionService: RegionService,
    @Inject(DOCUMENT) private document: Document,
    private cookieService: CookieService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private transferState: TransferState,
    @Optional() @Inject('REQUEST') private request: any,
    @Optional()
    @Inject('GEO_DEFAULT_LANG')
    private geoDefaultLang: string | null,
  ) {
    const langs = ['en', 'ar'];
    this.translate.addLangs(langs);
    const lang = this.getLocale();
    const used = langs.includes(lang) ? lang : 'en';
    this.translate.use(used);
    // Hand the resolved language to the browser pass — hydration can't see
    // Accept-Language or geo, so re-detecting there would flip the page.
    // Only for real requests: a build-time prerender pass (no REQUEST) must
    // not bake its fallback language into the snapshot's TransferState.
    if (!isPlatformBrowser(this.platformId) && this.request) {
      this.transferState.set(SSR_LANG_KEY, used);
    }
    this.handleLangChange(lang);

    this.translate.onLangChange.subscribe((event) => {
      this.handleLangChange(event.lang);
      if (isPlatformBrowser(this.platformId)) {
        const url = new URL(this.document.location.href);
        url.searchParams.set('lang', event.lang);
        window.history.pushState(null, '', url.toString());
      }
    });
  }

  private get cookiesOptions() {
    const expirationData = new Date();
    expirationData.setFullYear(expirationData.getFullYear() + 1);
    return {
      expires: expirationData,
    };
  }

  handleLangChange(_lang) {
    const lang = this.regionService.getLang(_lang);
    let ltrrtl = 'ltr';
    if (lang === 'ar') ltrrtl = 'rtl';
    else ltrrtl = 'ltr';

    this.document.getElementsByTagName('html')[0].setAttribute('lang', lang);
    this.document.getElementsByTagName('body')[0].setAttribute('dir', ltrrtl);
    this.saveLocale(_lang);
  }

  saveLocale(locale) {
    if (isPlatformBrowser(this.platformId))
      this.cookieService.put(this.storageKey, locale, {
        ...this.cookiesOptions,
      });
  }

  /**
   * Resolution order: `?lang=` param → `uptal-locale` cookie → Arabic
   * Accept-Language / browser language → geo default (KSA → `ar`) → `en`.
   * Explicit choices (param, cookie) always win; the geo tier only ever
   * applies to a visitor the site has never seen make one.
   */
  getLocale() {
    const queryParams = new URLSearchParams(this.document.location.search);
    const explicit =
      queryParams.get('lang') ?? this.cookieService.get(this.storageKey);
    if (explicit) return explicit;

    if (isPlatformBrowser(this.platformId)) {
      const ssrLang = this.transferState.get(SSR_LANG_KEY, '');
      if (ssrLang) return ssrLang;
      // No SSR pass (plain `ng serve`) — fall back to the browser language.
      return window.navigator.language?.split('-')[0] ?? 'en';
    }

    if (this.acceptsArabic()) return 'ar';
    return this.geoDefaultLang ?? 'en';
  }

  /** True when the request's Accept-Language lists Arabic at any priority. */
  private acceptsArabic(): boolean {
    const header = String(this.request?.headers?.['accept-language'] ?? '');
    return header
      .split(',')
      .some(
        (part) =>
          part.trim().toLowerCase().split(';')[0].split('-')[0] === 'ar',
      );
  }
}
